import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  Check,
  Home,
  Landmark,
  Leaf,
  Sparkles,
  Tractor,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../../components/layout/Container';
import { Reveal } from '../../components/motion/Reveal';
import { MeshBackground } from '../../components/motion/MeshBackground';
import { Badge } from '../../components/ui/badge';
import { FilterPill } from '../../components/ui/filter-pill';
import { useStoredAudienceId } from '../../hooks/useActiveAudience';
import type { AudienceId } from '@shared/constants/audiences';
import {
  TOOLS_CATALOG,
  TOOL_CATEGORIES,
  getRelevantToolsForAudience,
  getToolHref,
  isToolRecommendedFor,
  type ToolCategory,
  type ToolDefinition,
  type ToolTone,
} from '@shared/constants/tools-catalog';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';

/* ────────────────────────────────────────────────────────
   Filtres profil — alignés sur les AudienceId pluriel
──────────────────────────────────────────────────────── */
type AudienceFilter = AudienceId | 'all';

const AUDIENCE_FILTERS: { id: AudienceFilter; label: string; icon: LucideIcon }[] = [
  { id: 'all', label: 'Tous les outils', icon: Leaf },
  { id: 'particuliers', label: 'Particuliers', icon: Home },
  { id: 'professionnels', label: 'Professionnels', icon: Users },
  { id: 'entreprises', label: 'Entreprises', icon: Building2 },
  { id: 'coproprietes', label: 'Copropriétés', icon: Building2 },
  { id: 'collectivites', label: 'Collectivités', icon: Landmark },
  { id: 'artisans', label: 'Artisans RGE', icon: Wrench },
  { id: 'agriculteurs', label: 'Agriculteurs', icon: Tractor },
];

/* ────────────────────────────────────────────────────────
   Tons (mappés sur les clés Tailwind du design system)
──────────────────────────────────────────────────────── */
const TONE_CLASSES: Record<
  ToolTone,
  {
    iconBg: string;
    metricColor: string;
    dot: string;
    badge: string;
    ctaBg: string;
    border: string;
    halo: string;
    recommendedRing: string;
  }
> = {
  accent: {
    iconBg: 'bg-accent-50 text-accent-700',
    metricColor: 'text-accent-700',
    dot: 'bg-accent-500',
    badge: 'bg-accent-50 text-accent-800 ring-accent-200/50',
    ctaBg: 'bg-accent-500 hover:bg-accent-400 text-ink',
    border: 'hover:border-accent-300/60',
    halo: 'group-hover:shadow-[0_8px_32px_rgba(132,204,22,0.10)]',
    recommendedRing: 'ring-accent-300/60 bg-accent-50/20',
  },
  brand: {
    iconBg: 'bg-brand-50 text-brand-700',
    metricColor: 'text-brand-700',
    dot: 'bg-brand-500',
    badge: 'bg-brand-50 text-brand-800 ring-brand-200/50',
    ctaBg: 'bg-brand-600 hover:bg-brand-700 text-white',
    border: 'hover:border-brand-300/60',
    halo: 'group-hover:shadow-[0_8px_32px_rgba(16,185,129,0.10)]',
    recommendedRing: 'ring-brand-300/60 bg-brand-50/20',
  },
  lavender: {
    iconBg: 'bg-lavender-50 text-lavender-700',
    metricColor: 'text-lavender-700',
    dot: 'bg-lavender-500',
    badge: 'bg-lavender-50 text-lavender-800 ring-lavender-200/50',
    ctaBg: 'bg-lavender-600 hover:bg-lavender-700 text-white',
    border: 'hover:border-lavender-300/60',
    halo: 'group-hover:shadow-[0_8px_32px_rgba(139,92,246,0.08)]',
    recommendedRing: 'ring-lavender-300/60 bg-lavender-50/20',
  },
  sage: {
    iconBg: 'bg-sage-100 text-sage-500',
    metricColor: 'text-sage-500',
    dot: 'bg-sage-500',
    badge: 'bg-stone-100 text-stone-700 ring-stone-200/50',
    ctaBg: 'bg-ink hover:bg-stone-800 text-white',
    border: 'hover:border-stone-300',
    halo: 'group-hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]',
    recommendedRing: 'ring-stone-300/60 bg-stone-50/40',
  },
} as const;

/* ────────────────────────────────────────────────────────
   Page
──────────────────────────────────────────────────────── */
export default function OutilsPage() {
  const storedAudience = useStoredAudienceId();
  const [activeFilter, setActiveFilter] = useState<AudienceFilter>('all');
  const [userOverrode, setUserOverrode] = useState(false);

  useEffect(() => {
    if (userOverrode) return;
    if (storedAudience) {
      setActiveFilter(storedAudience);
    } else {
      setActiveFilter('all');
    }
  }, [storedAudience, userOverrode]);

  const audienceForFilter: AudienceId | null = activeFilter === 'all' ? null : activeFilter;

  const filtered = useMemo(
    () => getRelevantToolsForAudience(audienceForFilter),
    [audienceForFilter],
  );

  const grouped = useMemo(() => groupByCategory(filtered), [filtered]);

  const recommendedCount = useMemo(
    () =>
      audienceForFilter
        ? filtered.filter((t) => isToolRecommendedFor(t.id, audienceForFilter)).length
        : 0,
    [filtered, audienceForFilter],
  );

  const handleFilterClick = (id: AudienceFilter) => {
    setUserOverrode(true);
    setActiveFilter(id);
  };

  return (
    <div className="relative min-h-screen bg-paper">
      <MeshBackground variant="warm" intensity="soft" withGrid />

      {/* ── Hero ── */}
      <Container className="relative pt-16 pb-10 md:pt-20 md:pb-14">
        <Reveal>
          <Badge variant="ivory" size="lg" className="gap-2">
            <Leaf className="h-3.5 w-3.5 text-brand-600" />
            Accès libre &amp; gratuit
          </Badge>
          <h1 className="mt-5 h-hero-lg">
            Tous vos outils{' '}
            <span className="text-gradient">énergie &amp; CEE</span>
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-stone-600">
            Estimateurs, comparateurs, audit décret tertiaire. Chiffres documentés,
            accès libre, sans inscription. Sélectionnez votre profil — on vous affiche
            les outils qui s’appliquent à votre situation.
          </p>
        </Reveal>

        {/* ── Audience filter ── */}
        <Reveal delay={0.08} className="mt-8">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtrer par profil">
            {AUDIENCE_FILTERS.map((aud) => {
              const active = activeFilter === aud.id;
              return (
                <FilterPill
                  key={aud.id}
                  role="tab"
                  aria-selected={active}
                  active={active}
                  icon={aud.icon}
                  onClick={() => handleFilterClick(aud.id)}
                >
                  {aud.label}
                </FilterPill>
              );
            })}
          </div>
        </Reveal>

        {/* ── Bandeau personnalisation ── */}
        <AnimatePresence>
          {audienceForFilter && recommendedCount > 0 && (
            <motion.div
              key={audienceForFilter}
              role="status"
              aria-live="polite"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: easeStripe }}
              className="mt-6 flex items-start gap-3 rounded-xl border border-brand-200/60 bg-brand-50/40 px-4 py-3"
            >
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" strokeWidth={1.7} />
              <p className="text-[13px] leading-relaxed text-brand-800">
                <strong className="font-bold">
                  {recommendedCount} outil{recommendedCount > 1 ? 's' : ''} recommandé
                  {recommendedCount > 1 ? 's' : ''}
                </strong>{' '}
                pour votre profil. Les autres outils restent accessibles plus bas.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>

      {/* ── Tools grid ── */}
      <Container className="relative pb-24">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: easeStripe }}
            className="space-y-10"
          >
            {grouped.length === 0 && (
              <div className="py-16 text-center">
                <p className="text-stone-500">
                  Aucun outil disponible pour ce profil pour l’instant.
                </p>
              </div>
            )}

            {grouped.map((group) => (
              <section key={group.id}>
                <header className="mb-4 flex items-baseline justify-between gap-3 border-b border-stone-100 pb-2">
                  <h2 className="font-display text-[15px] font-semibold tracking-tight text-ink">
                    {group.label}
                  </h2>
                  <span className="text-[11px] text-stone-400">
                    {group.tools.length} outil{group.tools.length > 1 ? 's' : ''}
                  </span>
                </header>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {group.tools.map((tool, i) => (
                    <ToolCard
                      key={tool.id}
                      tool={tool}
                      index={i}
                      recommended={isToolRecommendedFor(tool.id, audienceForFilter)}
                      audienceId={audienceForFilter}
                    />
                  ))}
                </div>
              </section>
            ))}
          </motion.div>
        </AnimatePresence>
      </Container>

      {/* ── Trust strip ── */}
      <div className="border-t border-stone-200 bg-stone-50/70 py-10">
        <Container>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-[13px] text-stone-500">
            {[
              'Données publiques officielles',
              'Fiches CEE en vigueur',
              'Aucune inscription requise',
              'Chiffres documentés',
            ].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 font-medium">
                <Check className="h-3.5 w-3.5 text-brand-500" strokeWidth={2.5} />
                {t}
              </span>
            ))}
          </div>
        </Container>
      </div>

      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Tous les outils', url: 'https://solutionecofrance.fr/outils' },
        ]}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Group helper
──────────────────────────────────────────────────────── */
interface ToolGroup {
  id: ToolCategory;
  label: string;
  tools: ToolDefinition[];
}

function groupByCategory(tools: ToolDefinition[]): ToolGroup[] {
  const map = new Map<ToolCategory, ToolDefinition[]>();
  for (const t of tools) {
    const list = map.get(t.category) ?? [];
    list.push(t);
    map.set(t.category, list);
  }
  // ordre stable via TOOL_CATEGORIES, ne renvoie que les catégories non vides
  return TOOL_CATEGORIES.filter((c) => (map.get(c.id) ?? []).length > 0).map((c) => ({
    id: c.id,
    label: c.label,
    tools: map.get(c.id) ?? [],
  }));
}

/* ────────────────────────────────────────────────────────
   ToolCard
──────────────────────────────────────────────────────── */
function ToolCard({
  tool,
  index,
  recommended,
  audienceId,
}: {
  tool: ToolDefinition;
  index: number;
  recommended: boolean;
  audienceId: AudienceId | null;
}) {
  const Icon = getIcon(tool.iconName);
  const tc = TONE_CLASSES[tool.tone];
  const href = getToolHref(tool.id, audienceId);

  return (
    <motion.a
      href={href}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05, ease: easeStripe }}
      whileHover={{ y: -3 }}
      className={cn(
        'group flex flex-col rounded-2xl border bg-white p-6 transition-all duration-200 ease-out',
        recommended
          ? cn('border-2 ring-2 shadow-[0_1px_2px_rgba(10,10,10,.04)]', tc.recommendedRing)
          : 'border-stone-200',
        tc.border,
        tc.halo,
        tool.featured && !recommended && 'ring-1 ring-inset ring-stone-200',
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-150 ease-out group-hover:scale-105',
            tc.iconBg,
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.7} />
        </span>
        <div className="flex flex-wrap gap-1.5">
          {recommended && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white">
              <Sparkles className="h-2.5 w-2.5 text-accent-300" strokeWidth={2} />
              Recommandé
            </span>
          )}
          {tool.badge && !recommended && (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ring-1 ring-inset',
                tc.badge,
              )}
            >
              {tool.badge}
            </span>
          )}
          {tool.featured && !tool.badge && !recommended && (
            <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-600">
              Populaire
            </span>
          )}
        </div>
      </div>

      {/* Eyebrow + title */}
      <p className="mt-4 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-stone-500">
        {tool.eyebrow}
      </p>
      <h3 className="mt-1.5 font-display text-[17px] font-semibold leading-tight text-ink">
        {tool.title}
      </h3>
      <p className="mt-2 text-[13px] leading-relaxed text-stone-600">{tool.description}</p>

      {/* Bullets */}
      <ul className="mt-4 flex flex-wrap gap-1.5">
        {tool.bullets.map((b) => (
          <li
            key={b}
            className="inline-flex items-center gap-1 rounded-md bg-stone-100/70 px-2 py-0.5 text-[11px] font-medium text-stone-700"
          >
            <span className={cn('h-1 w-1 rounded-full', tc.dot)} />
            {b}
          </li>
        ))}
      </ul>

      {/* Footer */}
      <div className="mt-auto flex items-end justify-between gap-3 border-t border-stone-100 pt-4">
        <div>
          <p className={cn('font-display text-xl font-bold tabular-nums', tc.metricColor)}>
            {tool.metric.value}
          </p>
          <p className="text-[11px] text-stone-500">{tool.metric.label}</p>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-all duration-150 ease-out',
            tc.ctaBg,
          )}
        >
          {tool.cta ?? 'Accéder'}
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
      </div>
    </motion.a>
  );
}

// (Garde de typage : on s'assure que tous les outils du catalogue ont un ton supporté.)
const _toneCheck: ReadonlyArray<ToolTone> = TOOLS_CATALOG.map((t) => t.tone);
void _toneCheck;
